import { RpcClient, getStdRpcContext, initRpcClient } from "./RPCAxiosClient"
import { Sha1 } from "./sha1";

const defaultBrandConfig = {
    methodPrefix: "DDDI",
    extensionNamespace: "EUeInvoices",
    dropNamespace: false,
    textReplacements: {} as Record<string, string>,
};

export const brandConfig = { ...defaultBrandConfig };

export function resetBrandConfig(): void {
    Object.assign(brandConfig, { ...defaultBrandConfig, textReplacements: {} });
}

export function tx(text: string): string {
    const replacements = brandConfig.textReplacements;
    if (!replacements || Object.keys(replacements).length === 0) return text;
    let result = text;
    for (const [from, to] of Object.entries(replacements)) {
        result = result.replaceAll(from, to);
    }
    return result;
}

export const brandPostProcessor = {
    type: 'postProcessor' as const,
    name: 'brandReplacer',
    process(value: string): string {
        return tx(value);
    },
};

function resolveExtension(ext: string): string {
    if (!ext.includes("DDDI_")) return ext;
    const withPrefix = ext.replace("DDDI_", brandConfig.methodPrefix + "_");
    if (brandConfig.dropNamespace) {
        return withPrefix.replace(/^[^.]+\./, "");
    }
    return withPrefix.replace("EUeInvoices", brandConfig.extensionNamespace);
}

export class DDD{
    username : string;
    password : string;
    url : string;
    isLogged : boolean;

    /*Connection key requests circumvent the normal api request behaviours. They use 3 request headers:
    D3Pro-PreferAuthorizationOverSession:boolean
    Authorization:string
    D3Pro-NoSession:boolean

    if only D3Pro-PreferAuthorizationOverSession and Authorization are set. The existing cookie session will be overriden. So for our desired outcome
    all 3 headers must be set.
    If only cookie and Authorization headers are set. Server ignores Authorization.
    */
    connectionKey : string|null;
    preferAuthorizationOverSession : boolean;
    noSession : boolean;
    _rpcClient : any|null;

    customRpc : boolean;


    public constructor(url?:string);
    constructor(url?: string, username? : string, password? : string){
        this.username = username||'';
        this.password = password||'';
        this.url = url||"";
        this.isLogged = false;
        this.connectionKey = null;
        this.preferAuthorizationOverSession = false;
        this.noSession = false;
        this.customRpc = false;
        
        if(this.url)
            RpcClient.config.url = this.url;
    }

    static WithConnectionKey(url:string, connectionKey:string, PreferAuthorizationOverSession:boolean = true, NoSession:boolean = true){
        const ret = new DDD(url);
        ret.url = url;
        ret.connectionKey = connectionKey;
        ret.preferAuthorizationOverSession = PreferAuthorizationOverSession;
        ret.noSession = NoSession;

        ret.getRpcClient().config.url = url;
        return ret;
    }

    static WithCustomRpcClient(url:string){
        const ret = new DDD(url);
        ret.url = url;
        ret.customRpc = true;

        ret.getRpcClient().config.url = url;
        return ret;
    }
    
    
    public SetUrl = (url:string) : void => {
        this.url = url;
        this.getRpcClient().config.url = this.url;
    }

    public Login = () : Promise<boolean|string> => {
        if(this.isLogged)
            return new Promise<boolean|string>((resolve) => {resolve(true)});

        return new Promise<boolean|string>((resolve, reject) =>{
            let that = this;
            this.getRpcClient().call("Auth.IdentifyUsername", this.username).done(function(e:any){
                if(!e.result)
                    return reject(e);

                var challenge = e.result;
                var fixedSalt = Sha1.hash(that.naiveReverse(that.username),true);
                var fixedHash = Sha1.hash(that.password+fixedSalt, true);
                var proof = Sha1.hash(fixedHash+challenge);
                
                that.getRpcClient().call("Auth.AuthenticateUsername", {challenge : challenge, proof : proof, persist : true}).done(function(e:any){
                    if(e.result === "ok"){
                        that.isLogged = true;
                        resolve(e);
                    }else{
                        reject(e);
                    }
                }).post(getStdRpcContext());
            }).post(getStdRpcContext());
        });
    }

    public ChangePassword = (username:string, newPassword:string, newPassword2:string) : Promise<any> => {
        let that = this;
        return new Promise<boolean|string>((resolve, reject) =>{
            that.getRpcClient().call("Auth.IdentifyUsername", username).done(function(e:any){
                if(!e.result)
                    return reject(e);
                var salt1 = e.result;
                var salt0 = Sha1.hash(username.toLowerCase().split('').reverse().join(''), true);
                var params = {
                    challenge: salt1,
                    proof: Sha1.hash(newPassword + salt0, true),
                    verification: Sha1.hash(Sha1.hash(newPassword2 + salt0, true) + salt1, true)
                };
                that.getRpcClient().call("Auth.ChangePassword", params).done(function(e:any){
                    resolve(e);
                }).post(getStdRpcContext());
            }).post(getStdRpcContext());
            });
    }

    public AddUser = (args:any) : Promise<any> => {
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Auth.AddUser", args).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public UIC_Init = () : Promise<any[]> => {
        return new Promise<any[]>((resolve, reject) => {
            let editInfo : any|undefined = undefined;
            let calcInfo : any|undefined = undefined;

            this.getRpcClient().call("Extension.Execute",{extension : "EUeInvoices.UI_GetEditInfo", args : {Complexity: "Minimal"}}).done(function(response:any){
                editInfo = response?.result?.Result;
                if(calcInfo !== undefined)
                    resolve([editInfo, calcInfo]);
            }).post(getStdRpcContext());
            this.getRpcClient().call("Extension.Execute",{extension : "EUeInvoices.UI_GetCalcInfo", args : {}}).done(function(response:any){
                calcInfo = response?.result?.Result;
                if(editInfo !== undefined)
                    resolve([editInfo, calcInfo]);
            }).post(getStdRpcContext());
        })
    }

    public DDDI_GetNew = (args:any) : Promise<any> => {
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Extension.Execute",{extension : resolveExtension("EUeInvoices.DDDI_GetNew"), args : args||{}}).done(function(response:any){
                if(response?.result?.Status === "Error")
                    return reject(response?.result?.Reason)

                resolve(response?.result?.Result);
            }).post();
        });
    }

    public DDDI_Save = (args:any) : Promise<any> => {
        let ret= new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Extension.Execute",{extension : resolveExtension("EUeInvoices.DDDI_Save"), args : args||{}}).done(function(response:any){
                if(response?.error)
                    return resolve(response);
                resolve(response?.result);
            }).post(getStdRpcContext());
            
        });
        return ret;
    }

    public DDDI_ExecuteSteps = (args:any) : Promise<any> => {
        let ret= new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Extension.Execute",{extension : resolveExtension("EUeInvoices.DDDI_ExecuteSteps"), args : args||{}}).done(function(response:any){
                if(response?.error)
                    return resolve(response);
                resolve(response?.result);
            }).post(getStdRpcContext());
            
        });
        return ret;
    }

    public DDDI_TransformedDocs = (args:any) : Promise<any> => {
        let ret= new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Extension.Execute",{extension : resolveExtension("EUeInvoices.DDDI_TransformedDocs"), args : args||{}}).done(function(response:any){
                if(response?.error)
                    return resolve(response);
                resolve(response?.result);
            }).post(getStdRpcContext());
            
        });
        return ret;
    }

    public UI_GetNew = (args:any) : Promise<any> => {
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Extension.Execute",{extension : "EUeInvoices.UI_GetNew", args : args||{}}).done(function(response:any){
                if(response?.result?.Status === "Error")
                    return reject(response?.result?.Reason)

                resolve(response?.result?.Result);
            }).post(getStdRpcContext());
        });
    }

    public UI_GetNewFromExisting = (id:string) : Promise<any> =>{
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Extension.Execute",{extension : "EUeInvoices.UI_GetNewFromExisting", args : {Id: id}}).done(function(response:any){
                resolve(response?.result?.Result);
            }).post(getStdRpcContext());
        });
    }

    public UI_GetExisting = (id:string) : Promise<any> => {
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Extension.Execute",{extension : "EUeInvoices.UI_GetExisting", args : {Id: id}}).done(function(response:any){
                resolve(response?.result?.Result);
            }).post(getStdRpcContext());
        });
    }

    public APP_GetNewDDDIAndUIMerged = (dddiArgs:any, uiArgs:any) : Promise<any> => {
        return new Promise<any>((resolve, reject)=> {
            let dddiResponse:any = null;
            let uiResponse:any = null;
            let rejected = false;

            this.DDDI_GetNew(dddiArgs).then((data:any) => {
                dddiResponse = data;
                if(dddiResponse && uiResponse)
                    resolve({"DDDI_GetNew" : dddiResponse, "UI_GetNew" : uiResponse});
            }).catch((msg) => {
                if(rejected)
                    return;
                rejected=true;
                console.log("rejecting1")
                reject(msg);
            });
            this.UI_GetNew(uiArgs).then((data:any) => {
                uiResponse = data;
                if(dddiResponse && uiResponse)
                    resolve({"DDDI_GetNew" : dddiResponse, "UI_GetNew" : uiResponse});
            }).catch((msg) => {
                if(rejected)
                    return;
                rejected=true;
                console.log("rejecting1")
                reject(msg);
            });
        });
    }

    public APP_GetNewAndExisting = (newArgs:any, existingArgs:any) : Promise<any> => {
        return new Promise<any>((resolve, reject)=> {
            let dddiResponse:any = null;
            let uiResponse:any = null;
            let rejected = false;

            this.DDDI_GetNew(newArgs).then((data:any) => {console.log(data);
                dddiResponse = data;
                if(dddiResponse && uiResponse)
                    resolve({"DDDI_GetNew" : dddiResponse, "UI_GetExisting" : uiResponse});
            }).catch((msg) => {
                if(rejected)
                    return;
                rejected=true;
                reject(msg);
            });
            this.UI_GetExisting(existingArgs).then((data:any) => {console.log(data);
                uiResponse = data;
                if(dddiResponse && uiResponse)
                    resolve({"DDDI_GetNew" : dddiResponse, "UI_GetExisting" : uiResponse});
            }).catch((msg) => {
                if(rejected)
                    return;
                rejected=true;
                reject(msg);
            });
        });
    }

    public Rpc_ExecuteExtension = (extension:string, args:any) : Promise<any> => {
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Extension.Execute",{extension : resolveExtension(extension), args : args}).done(function(response:any){
            //this.getRpcClient().call(extension, args).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public Rpc_ExecuteMethod = (method:string, args:any) : Promise<any> => {
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call(method,args).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public ChangeOrganization = (orgId:string) : Promise<any> => {
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Auth.ChangeOrganization",{orgid : orgId}).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public GetSessionInfo = (args:any) : Promise<any> =>{
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Auth.GetSessionInfo", args).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public GetSessionAuthDescribe = (args:any) : Promise<any> =>{
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Auth.Describe", args).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public InvalidateSessionExtensions = () : Promise<any> =>{
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Metadata.InvalidateSessionExtensions", {user:"*", organization:"*"}).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public getRpcClient = () : any => {
        if(this.customRpc && !this.connectionKey){
            if(this._rpcClient)
                return this._rpcClient;

            this._rpcClient = initRpcClient();
            return this._rpcClient;
        }

        if(this.connectionKey){
            if(this._rpcClient)
                return this._rpcClient;

            this._rpcClient = initRpcClient();
            this._rpcClient.config.headers = {};
            this._rpcClient.config.headers['Authorization'] = `IoT ${this.connectionKey}:EUeInvoices`;
            this._rpcClient.config.headers['D3Pro-PreferAuthorizationOverSession'] = `True`;
            this._rpcClient.config.headers['D3Pro-NoSession'] = `True`;
            return this._rpcClient;
        }

        return RpcClient;
    }

    private naiveReverse(val:string) : string{
        return val.split('').reverse().join('');
    }
}

export class E2Data{
    entity : string|null = null;
    filter : string|null = null;
    orderBy : string|null = null;
    skip : number|null = 0;
    take : number|null = 100;
    component : string|null = null;
    format : "Flat"|"Full" = "Flat";
    includeCount : boolean = false;
    values : any = null;
    select : string[]|null = null;

    //
    connectionKey : string|null = null;
    _rpcClient : any|null = null;

    constructor(entity:string){
        this.entity = entity;
    }

    static WithoutConnectionKey(url:string, entity:string){
        const ret = new E2Data(entity);
        ret.entity = entity;

        ret.getRpcClient().config.url = url;
        return ret;
    }

    static WithConnectionKey(url:string, connectionKey:string, entity:string){
        const ret = new E2Data(entity);
        ret.entity = entity;
        ret.connectionKey = connectionKey;

        ret.getRpcClient().config.url = url;
        return ret;
    }

    public ApplyParams = (params: Partial<E2Data>) => {
        Object.keys(params).forEach((key) => {
            if (key in this) {
                (this as any)[key] = params[key as keyof E2Data];
            }
        });
        return this;
    };

    public GetParamsObject = () => {
        let ret:any = {};
        ret.entity = this.entity;
        ret.filter = this.filter;
        ret.orderBy = this.orderBy;
        ret.skip = this.skip;
        ret.take = this.take;
        ret.format = this.format;
        ret.includeCount = this.includeCount;
        if(this.connectionKey)
            ret.connectionKey = this.connectionKey;
        if(this.component)
            ret.component = this.component;
        if(this.select)
            ret.select = this.select;
        return ret;
    }

    public GetData = () : Promise<any> => {
        let method = "Data.GetEntityData";
        let params:any = {};
        params.entity = this.entity;
        params.skip =  this.skip;
        params.take =  this.take;
        params.format = this.format || "Flat";
        if(this.filter)
            params.filter = this.filter;
        if(this.orderBy)
            params.orderBy = this.orderBy;
        if(this.includeCount)
            params.includeCount = true;
        if(this.component)
            params.component = this.component;
        if(this.select)
            params.select = this.select;

        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call(method,params).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public Modify = () : Promise<any> =>{
        let method = "Data.Modify";
        let params:any = {};
        params.entity = this.entity;
        params.filter = this.filter;
        params.values = this.values || {};
        params.format = this.format || "Flat";
        if(this.component)
            params.component = this.component;

        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call(method,params).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public Create = () : Promise<any> => {
        let method = "Data.Create";
        let params:any = {};
        params.entity = this.entity;
        params.values = this.values || {};
        params.format = this.format || "Flat";
        if(this.component)
            params.component = this.component;

        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call(method,params).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public GetEntityInfo = () : Promise<any> => {
        let method = "Data.GetEntityInfo";
        let params:any = {};
        params.entity = this.entity;
        params.format = this.format || "Flat";
        if(this.component)
            params.component = this.component;

        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call(method,params).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public Delete = () : Promise<any> =>{
        let method = "Data.Delete";
        let params:any = {};
        params.entity = this.entity;
        params.filter = this.filter;
        if(this.component)
            params.component = this.component;

        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call(method,params).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public Delay = () : Promise<any> =>{
        return new Promise<any>((resolve, reject)=> {
            this.getRpcClient().call("Delay", { "duration": "00:00:10" }).done(function(response:any){
                resolve(response);
            }).post(getStdRpcContext());
        });
    }

    public getRpcClient = () : any => {
        //console.log(RpcClient);
        if(this.connectionKey){
            if(this._rpcClient)
                return this._rpcClient;

            this._rpcClient = initRpcClient();
            this._rpcClient.config.headers = {};
            this._rpcClient.config.headers['Authorization'] = `IoT ${this.connectionKey}:EUeInvoices`;
            this._rpcClient.config.headers['D3Pro-PreferAuthorizationOverSession'] = `True`;
            this._rpcClient.config.headers['D3Pro-NoSession'] = `True`;
            return this._rpcClient;
        }

        return RpcClient;
    }
}



export function copyExistingToNew(blank:any, existing:any){
    if(!blank || !existing)
        return console.error("Missing arguments");
    let ret:any = {};
    let details:any = {...blank._details};
    delete blank._details;

    for(let key in blank){
        if(existing.hasOwnProperty(key))
            ret[key] = existing[key].Value;
    }

    if(!existing._details)
        return ret;

    ret._details = {};
    for(let [key, val] of Object.entries(details)){
        if(!existing._details.hasOwnProperty(key)){
            ret._details[key] = val;
            continue;
        }

        ret._details[key] = [];
        existing._details[key].forEach((item:any) => {
            let newChild = {...details[key][0]};
            for(let key in newChild){
                if(item.hasOwnProperty(key))
                    newChild[key] = item[key].Value;
            }
            ret._details[key].push(newChild)
        });
    }
    return ret;
}

function compareObjectKeys(obj1:any, obj2:any, path:string){
    for(const key in obj1){
        if(!obj2.hasOwnProperty(key)){
            console.log("Missing: "+path+"."+key)
            continue;
        }

        if(Array.isArray(obj1[key])){
            if(!Array.isArray(obj2[key])){
                console.log("Both not array: " +path+"."+key)
                continue;
            }
            if(obj1[key].length !== obj2[key].length){
                console.log("Array length: " +path+"."+key)
                continue;
            }

            for(let i=0;i<obj1[key].length;i++){
                compareObjectKeys(obj1[key][i], obj2[key][i], path+"."+key);
            }
        }

        if(key === "_details"){
            compareObjectKeys(obj1[key], obj2[key], path+"."+key)
        }
    }
}
export function mergeDDDIAndUI(dddiObj:any, UiObj:any){
    let dddi = dddiObj.Invoice;
    let ui = UiObj;
    let ret:any = {};

    for(const row in dddi){
        if(row==="_details"){
            ret["_details"] = {};
            for(let item in dddi["_details"]){
                ret["_details"][item] = [];
                for(let i=0;i<dddi["_details"][item].length;i++){
                    let detObj = dddi["_details"][item][i];
                    let uiObj = ui["_details"] && ui["_details"][item] && ui["_details"][item][i];
                    ret["_details"][item].push(mergeDddiAndUi(detObj, uiObj))
                }
            } 
        }else{
            ret[row] = {};
            ret[row].defaultValue = dddi[row];
            if(ui[row])
                ret[row] = {...ret[row], ...ui[row]};
        }
    }
    return ret;
}

function mergeDddiAndUi(dddi:any,ui:any){
    let ret:any={};
    for(const row in dddi){
        ret[row] = {};
        ret[row].defaultValue = dddi[row];
        if(ui[row])
                ret[row] = {...ret[row], ...ui[row]};
    }

    return ret;
}


/**
 * Merges object from EUeInvoices.DDDI_GetNew and EUeInvoices.UI_GetNew
 * @param {*} dddiObj expects : {Invoice : {...fieldNames:string|null|etc}}
 * @param {*} UiObj  expects : {...fieldNames:{}}
 * @returns {} {...fieldNames:{}} defaultValue from dddiObj is added to all the UI extra data
 */
export function mergeDDDIAndUI2(dddiObj:any, UiObj:any){
    //console.log(dddiObj);
    //console.log(UiObj)
    /*compareObjectKeys(dddiObj.Invoice,UiObj, "");
    console.log(222)
    compareObjectKeys(UiObj,dddiObj.Invoice, "");*/

    let newObj = JSON.parse(JSON.stringify(dddiObj)); // obj1
    let combinedObj:any = {};
    for (const row in UiObj) {
        if (newObj.Invoice[row] !== "undefined") {
        if (row !== "_details") {
            let valueRow1 = { defaultValue: newObj.Invoice[row] };
            let valueRow2 = UiObj[row];
            combinedObj[row] = mergeKey(valueRow1, valueRow2);
        } else {
            combinedObj["_details"] = {};
            for (let item in newObj.Invoice[row]) {
            combinedObj["_details"] = {
                ...combinedObj["_details"],
                [item]: [],
            };
            for (let subObj of UiObj[row][item]) {
                let tmpObj = { ...subObj };
                for(let k in tmpObj)
                    tmpObj[k].defaultValue = null;
                if(newObj.Invoice["_details"][item] && newObj.Invoice["_details"][item].length > 0){
                    for(let fieldN in newObj.Invoice["_details"][item][0]){
                        if(tmpObj[fieldN])
                            tmpObj[fieldN].defaultValue = newObj.Invoice["_details"][item][0][fieldN];
                    }
                }
                
                combinedObj["_details"][item].push({ ...subObj });
            }
            }
        }
        }
    }
    return combinedObj;
}
/**
 * Generates invoice of values + _details object. The merged object moved values from DDDI response to fieldName.defualtValue.
 * This function generates the same object as from DDDI response, but it fills it with fieldName.Value 
 * @param dddiUiObj return from the mergeDDDIAndUI function: {...fieldNames:{}} defaultValue from dddiObj is added to all the UI extra data
 * @returns 
 */
export function getInvoiceValuesFromMergedDDDIAndUI(dddiUiObj:any){
    let filteredObj:any = {};
    for (const row in dddiUiObj) {
      if (row !== "_details") {
        let valueRow1 = { defaultValue: dddiUiObj[row].Value };
        filteredObj[row] = getValue(dddiUiObj[row]);
      } else {
        filteredObj["_details"] = {};
        for (let item in dddiUiObj[row]) {
          filteredObj["_details"] = {...filteredObj["_details"],[item]: []};
          for (let [subObjIndex, subObj] of dddiUiObj[row][item].entries()) {
            filteredObj["_details"][item].push({});
            for (let subKey in subObj) {
              if(subKey === "_details"){
                filteredObj["_details"][item][subObjIndex]["_details"] = handleDetails(subObj["_details"]);
              }else{
                filteredObj["_details"][item][subObjIndex][subKey] = getValue(dddiUiObj[row][item][subObjIndex][subKey]);
              }
            }
          }
        }
      }
    }
    return filteredObj;
}

const handleDetails = (details:any) => {
    let ret:any = {};
    for(let subSubObjIndex in details){
        if(subSubObjIndex === "defaultValue") continue;
        ret[subSubObjIndex] = [];
        for(let subSubObj in details[subSubObjIndex]){
            let obj = details[subSubObjIndex][subSubObj];
            if(!obj || Object.keys(obj).length === 0) continue;
            let retObj:any = {};
            for(let key in obj){
                retObj[key] = getValue(obj[key]);
            }
            ret[subSubObjIndex].push(retObj);
        }   
    }
    return ret;
}

const getValue =  (obj:any) => {
    if(!obj)
     return null;

    if(obj.hasOwnProperty("Value") && obj.Value !== undefined)
        return obj.Value;
    if(obj.hasOwnProperty("defaultValue") && obj.defaultValue !== undefined)
        return obj.defaultValue;
    return null;    
}

  
const mergeKey = (value1:any, value2:any) => {
    return Object.assign({}, value1, value2);
};
